// src/features/Management/page/UserFormPage.tsx
import { CheckEmail, CheckUserName } from '../../../../common/services/realTimeCheckAPI';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import DynamicForm, { type DynamicFormSchema } from '@/common/components/DynamicForm/DynamicForm';
import { useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

export default function UserForm() {
    const { t } = useTranslation();

    // const { state } = useRouter().state.location;
    // const id = state && (state as { id?: string }).id ? Number((state as { id?: string }).id) : undefined;
    const { state } = useRouter().state.location;

    const record = (state as { record?: Record<string, unknown> })?.record;

    const id = record && record.id !== undefined && record.id !== null
        ? Number(record.id)
        : undefined;

    const initialUserType = record?.userType;

    const userFormSchema: DynamicFormSchema = {
        layout: 'vertical', // Overall form layout
        // submitButtonText: 'Save User', // Custom text for the submit button
        showResetButton: true, // Show a reset button

        // Define form sections for better organization and complex layouts
        sections: [
            {
                key: 'user-details', // Unique key for the section
                title: 'User Details', // Title displayed if type is 'card'
                type: 'card', // This section will be rendered inside an Ant Design Card
                layout: { type: 'grid', gutter: 24 }, // Fields within this section use a 2-column grid with 24px gutter
                fields: [
                    {
                        name: "userType",
                        label: "User Type",
                        type: "radio",
                        colSpan: 24,
                        disabled: initialUserType != "system" && id !== undefined,
                        options: [
                            { label: "System", value: "system" },
                            { label: "Customer", value: "customer" },
                            { label: "Company", value: "company" },
                            { label: "Medical Provider", value: "medical" },
                        ],
                    },
                    {
                        name: 'customerId',
                        label: 'Customer',
                        type: 'select',
                        colSpan: 24,
                        required: [{ field: "userType", operator: "===", value: "customer" }],
                        requiredMessage: 'Please select a Customer.',
                        placeholder: "Select Customer",
                        dependencies: [{ field: "userType", operator: "===", value: "customer" }],
                        disabled: id !== undefined && initialUserType !== "system",
                        dynamicOptions: {
                            url: "/Lookup/GetDropdownData",
                            method: "POST",
                            body: { dropdowns: ["customer"] },
                            transformData: "(apiResponse) => apiResponse.customer.map(item => ({ label: item.Name, value: item.Id }))"
                        }
                    },
                    {
                        name: 'companyId',
                        label: 'Company',
                        type: 'select',
                        colSpan: 24, // Full width
                        required: [{ "field": "userType", "operator": "===", "value": "company" }],
                        requiredMessage: 'Please select a company.', // Custom required message
                        placeholder: "Select Company",
                        dependencies: [
                            { "field": "userType", "operator": "===", "value": "company" }
                        ],
                        disabled: id !== undefined && initialUserType !== "system",
                        dynamicOptions: {
                            url: "/Lookup/GetDropdownData",
                            method: "POST",
                            body: { "dropdowns": ["company"] },
                            transformData: "(apiResponse) => apiResponse.company.map(item => ({ label: item.Name, value: item.Id }))"
                        },
                    },
                    {
                        name: 'medicalProviderId',
                        label: 'Medical Provider',
                        type: 'select',
                        required: [{ "field": "userType", "operator": "===", "value": "medical" }],
                        requiredMessage: 'Please select a medical provider.', // Custom required message
                        colSpan: 24, // Full width
                        placeholder: "Select Medical Provider",
                        dependencies: [
                            { "field": "userType", "operator": "===", "value": "medical" }
                        ],
                        disabled: id !== undefined && initialUserType !== "system",
                        options: [], // Keep this empty as options will be dynamic
                        dynamicOptions: { // Configuration for fetching options from an API
                            url: "/Lookup/GetDropdownData", // Your API endpoint for skills (ensure it returns an array of objects like {id: 1, name: "JavaScript"})
                            method: "POST", // HTTP method for the request
                            body: { "dropdowns": ["medicalprovider"] },
                            // JavaScript function string to transform the API response into { label, value } format
                            transformData: "(apiResponse) => apiResponse.medicalprovider.map(item => ({ label: item.Name, value: item.Id }))"
                        },
                    },

                    {
                        name: 'Firstname',
                        label: 'First Name',
                        type: 'input',
                        required: [{ "field": "userType", "operator": "===", "value": "system" }],
                        requiredMessage: 'Please enter a First Name.', // Custom required message
                        autocomplete: 'false',
                        pattern: 'AlphabetsWithSpace',
                        validation: [
                            { min: 3, message: t('First Name must be at least 3 characters long.') },
                            { max: 50, message: t('First Name cannot be more than 50 characters long.') },
                        ],

                        colSpan: 12, // Takes 12 out of 24 grid units (half width)
                    },
                    {
                        name: 'LastName',
                        label: 'Last Name',
                        type: 'input',
                        required: [{ "field": "userType", "operator": "===", "value": "system" }],
                        requiredMessage: 'Please enter a Last Name.', // Custom required message
                        autocomplete: 'false',
                        pattern: 'AlphabetsWithSpace',
                        validation: [
                            { min: 3, message: t('Last Name must be at least 3 characters long.') },
                            { max: 50, message: t('Last Name cannot be more than 50 characters long.') },
                        ],

                        colSpan: 12, // Takes 12 out of 24 grid units (half width)
                    },
                    {
                        name: 'cellNo', label: 'Mobile Number', type: 'input', required: true, colSpan: 12,
                        pattern: 'PhoneNumber',
                    },
                    {
                        name: 'roles',
                        label: 'Role(s)',
                        type: 'select',
                        required: [{ field: "userType", operator: "===", value: "system" }],
                        requiredMessage: 'Please select a role.',
                        colSpan: 24,
                        placeholder: "Select Role(s)",
                        mode: "multiple",
                        options: [],
                        dependencies: [{ field: "userType", operator: "===", value: "system" }], // 👈 field only shown when userType === system
                        dynamicOptions: {
                            url: "/Lookup/GetDropdownData",
                            method: "POST",
                            body: { dropdowns: ["role"] },
                            transformData: "(apiResponse) => apiResponse.role.map(item => ({ label: item.Name, value: item.Id }))"
                        }
                    },
                    {
                        name: 'userName',
                        label: 'Username',
                        type: 'input',
                        readOnlyWhen: [{ "field": "id", "operator": ">", "value": "0" }],
                        required: true,
                        requiredMessage: 'Please enter a username.', // Custom required message
                        autocomplete: 'false',
                        pattern: 'Alphabets',
                        validation: [
                            { min: 3, message: t('Name must be at least 3 characters long.') },
                            { max: 50, message: t('Name cannot be more than 50 characters long.') },
                            {
                                // Async validator for checking UserName uniqueness
                                validator: async (_: any, value: string) => {
                                    if (!value) return Promise.resolve();
                                    if (value.length < 3) return Promise.resolve(); // Don't call API if less than 3 chars

                                    try {
                                        const result = await CheckUserName(value, id);
                                        if (result.exists) {
                                            return Promise.reject(t('UserName already exists.'));
                                        }
                                        return Promise.resolve();
                                    } catch (error: any) {
                                        console.error('Error validating UserName:', error);
                                        return Promise.reject(error.message || t('Error validating UserName.'));
                                    }

                                },

                            },
                        ],
                        hideOnEdit: false, // This field will be hidden when the form is in edit mode
                        colSpan: 12, // Takes 12 out of 24 grid units (half width)
                    },
                    {
                        name: 'email',
                        label: 'Email',
                        type: 'input',
                        required: true,
                        requiredMessage: t('An email is required to create a user.'),
                        pattern: 'Email',
                        validation: [
                            { max: 256, message: t('Email cannot be more than 256 characters long.') },
                            {
                                // Async validator for checking National ID No uniqueness
                                validator: async (_: any, value: string) => {
                                    if (!value) return Promise.resolve();
                                    if (value.length < 1) return Promise.resolve(); // Don't call API if less than 1 chars

                                    try {
                                        const result = await CheckEmail(value, id);
                                        if (result.exists) {
                                            return Promise.reject(t('Email already exists.'));
                                        }
                                        return Promise.resolve();
                                    } catch (error: any) {
                                        console.error('Error validating Email:', error);
                                        return Promise.reject(error.message || t('Error validating Email.'));
                                    }

                                },

                            },
                        ],
                        autocomplete: 'false',
                        colSpan: 12, // Half width
                    },
                    {
                        name: 'previousEmail',
                        label: 'Email',
                        type: 'input',
                        autocomplete: 'false',
                        colSpan: 12, // Half width
                        hidden: true
                    },
                    {
                        name: 'isActive',
                        label: 'Status',
                        hidden: true,
                        type: 'switch',
                        colSpan: 12, // Full width
                    },
                ],
            },

        ],
    };

    return (
        <DynamicForm
            schema={userFormSchema}
            initialValues={{
                userType: id ? initialUserType : "system",
            }}
            submitApiUrl={API_ENDPOINTS.MANAGEMENT.SAVE_USER}
            getByIdApiUrl={API_ENDPOINTS.MANAGEMENT.GET_USER_BY_ID}
            updateApiUrl={API_ENDPOINTS.MANAGEMENT.UPDATE_USER}
            refreshTableDataKey={QUERY_KEYS.MANAGEMENT.USERS}
        />
    );
}
